import { useTranslation } from "react-i18next";
import "./application_tracking_widget.scss";
import { useEffect, useState } from "react";
import { useEffectOnce } from "react-use";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import { Button } from "./button";
import { Body } from "./typography";
import { SnowplowEvent, useSnowplowTracker } from "~/hooks/use_snowplow_tracker";
import serverAssetUrl from "~/utils/asset_url.server";
import { serverOnly$ } from "vite-env-only/macros";

type WidgetState = "not_signed_in" | "dream_job_available" | "dream_job_not_available";

const ApplicationTrackingWidget = () => {
  const { t } = useTranslation("job_post");
  const [jobSeekersUrl, setJobSeekersUrl] = useState("");
  const [widgetState, setWidgetState] = useState<WidgetState>();
  const { preview_mode, button_shape } = useBoardConfiguration();
  const { trackEvent } = useSnowplowTracker();
  const assetUrl = serverOnly$(serverAssetUrl) || window.ENV.ASSET_URL;

  useEffectOnce(() => {
    if (!preview_mode) {
      fetch(`${window.ENV.JOB_SEEKERS_URL}/users/self`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      })
        .then((response) => {
          if (response.status === 401 || !response.ok) {
            setWidgetState("not_signed_in");
            return;
          }

          return response.json().then(({ dreamJobAvailable }) => {
            setWidgetState(dreamJobAvailable ? "dream_job_available" : "dream_job_not_available");
          });
        })
        .catch(() => {
          setWidgetState("not_signed_in");
        });
    }
  });

  useEffect(() => {
    !preview_mode &&
      widgetState &&
      trackEvent(SnowplowEvent.MyGreenhouseAdShown, {
        type: "track_your_status",
      });
  });

  useEffect(() => {
    setJobSeekersUrl(`${window.ENV.JOB_SEEKERS_URL}/users/sign_in?source=track_your_status`);
  }, []);

  const trackClick = () => {
    !preview_mode &&
      trackEvent(SnowplowEvent.MyGreenhouseAdClicked, {
        type: `track_your_status_${widgetState}`,
      });
  };

  return (
    widgetState && (
      <div className="application_tracking_widget">
        <div className="application_tracking_widget__icon">
          <img src={`${assetUrl}/greenhouse_white_g.svg`} alt="MyGreenhouse Application Tracking" />
        </div>
        <div className="application_tracking_widget__content">
          <div className="application_tracking_widget__content__header">
            <Body medium>{t(`application_tracking_widget.${widgetState}.header`)}</Body>
            <Body>{t(`application_tracking_widget.${widgetState}.body`)}</Body>
          </div>
          <Button
            secondary
            link
            href={preview_mode ? undefined : jobSeekersUrl}
            target="_blank"
            shape={button_shape}
            onClick={trackClick}
          >
            {t(`application_tracking_widget.${widgetState}.create_account`)}
          </Button>
        </div>
      </div>
    )
  );
};

export default ApplicationTrackingWidget;
